import { useSetAtom } from "jotai";
import { useAtomCallback } from "jotai/utils";
import { useCallback, useMemo } from "react";
import { logger } from "../../datadog/logger";
import { useAppDependenciesContext } from "../../state";
import { appJotaiStore } from "../../state/jotai";
import { withRetry, WithRetryOptions } from "../../utils";
import { VideoSDKAdapter } from "../core/adapter";
import {
  connectedAtAtom,
  hasBackgroundProcessorFailureAtom,
  roomAtom,
  videoSDKAdapterAtom,
} from "../core/atoms";
import { useStoredAudioVideoSettings } from "../core/hooks/useStoredAudioVideoSettings";
import { participantsAtom } from "../core/room/participant/atoms";
import {
  localAudioContextAtom,
  localVideoContextAtom,
} from "../core/room/participant/media/atoms";
import { RoomDelegator } from "../core/room/RoomDelegator";

export type VideoSDKVendor = "twilio" | "zoom";
const VIDEO_SDK_INITIALIZATION_RETRY_CONFIG: Readonly<WithRetryOptions> = {
  retries: 3,
  initialDelayMs: 500,
  backoffFactor: 2,
  onFailure({ attempt, isFinalAttempt, error }) {
    if (isFinalAttempt) {
      logger.error(
        `Video SDK initialization failed after ${attempt} attempts.`,
        {},
        error,
      );
      return;
    }
    logger.warn(
      `Video SDK initialization attempt ${attempt} failed. Retrying...`,
      {},
      error,
    );
  },
};

/**
 * Returns a callback that lazily loads and initializes the desired video SDK
 * adapter
 */
export function useInitializeVideoSDKAdapter() {
  const setAdapter = useSetAtom(videoSDKAdapterAtom);
  const { useApolloClient } = useAppDependenciesContext();
  const apolloClient = useApolloClient();

  // Using memo instead of callback because callback complains that the
  // function's dependencies are unknown: useCallback(withRetry(...), [...])
  return useMemo(
    () =>
      withRetry(async (vendor: VideoSDKVendor) => {
        switch (vendor) {
          case "twilio": {
            logger.info("Initializing Twilio Video SDK adapter");
            const { TwilioVideoSDKAdapter } = await import("../twilio/adapter");
            const newAdapter = new TwilioVideoSDKAdapter({
              apolloClient,
              jotaiStore: appJotaiStore,
            });
            setAdapter(newAdapter);
            break;
          }
          case "zoom": {
            logger.info("Initializing Zoom Video SDK adapter");
            const { ZoomVideoSDKAdapter } = await import("../zoom/adapter");
            const newAdapter = await ZoomVideoSDKAdapter.create({
              jotaiStore: appJotaiStore,
            });
            setAdapter(newAdapter);
            break;
          }
          default: {
            const _exhaustiveCheck: never = vendor;
            throw new Error(`Unsupported vendor: ${_exhaustiveCheck}`);
          }
        }
      }, VIDEO_SDK_INITIALIZATION_RETRY_CONFIG),
    [setAdapter, apolloClient],
  );
}

export function useConnectAudioVideoInputs() {
  const getAdapter = useAtomCallback(
    useCallback((get) => get(videoSDKAdapterAtom), []),
  );
  const setLocalVideoContext = useSetAtom(localVideoContextAtom);
  const setLocalAudioContext = useSetAtom(localAudioContextAtom);
  const setHasBackgroundProcessorFailure = useSetAtom(
    hasBackgroundProcessorFailureAtom,
  );
  const getLocalVideoContext = useAtomCallback(
    useCallback((get) => get(localVideoContextAtom), []),
  );
  const getLocalAudioContext = useAtomCallback(
    useCallback((get) => get(localAudioContextAtom), []),
  );
  const { setStoredAudioVideoSettings } = useStoredAudioVideoSettings();

  return useCallback(async (): Promise<void> => {
    return await getAdapter().connectAudioVideoInputs({
      setLocalVideoContext,
      setLocalAudioContext,
      getLocalAudioContext,
      getLocalVideoContext,
      setHasBackgroundProcessorFailure,
      setStoredAudioVideoSettings,
    });
  }, [
    getAdapter,
    setLocalVideoContext,
    setLocalAudioContext,
    getLocalAudioContext,
    getLocalVideoContext,
    setHasBackgroundProcessorFailure,
    setStoredAudioVideoSettings,
  ]);
}

export function useJoinRoom() {
  const getAdapter = useAtomCallback(
    useCallback((get) => get(videoSDKAdapterAtom), []),
  );
  const getLocalAudioContext = useAtomCallback(
    useCallback((get) => get(localAudioContextAtom), []),
  );
  const getLocalVideoContext = useAtomCallback(
    useCallback((get) => get(localVideoContextAtom), []),
  );

  const setRoom = useSetAtom(roomAtom);
  const setParticipants = useSetAtom(participantsAtom);
  const setConnectedAt = useSetAtom(connectedAtAtom);

  return useCallback(
    async (token: string, displayName: string): Promise<RoomDelegator> => {
      const room = await getAdapter().joinRoom(
        token,
        displayName,
        getLocalAudioContext(),
        getLocalVideoContext(),
      );

      setRoom(room);
      setParticipants(Array.from(room.remoteParticipants, ([_, p]) => p));
      setConnectedAt(Date.now());

      return room;
    },
    [
      getLocalAudioContext,
      getLocalVideoContext,
      getAdapter,
      setRoom,
      setParticipants,
      setConnectedAt,
    ],
  );
}

export function useLeaveRoom(): VideoSDKAdapter["leaveRoom"] {
  const getAdapter = useAtomCallback(
    useCallback((get) => get(videoSDKAdapterAtom), []),
  );

  return useCallback(
    async (...args) => {
      return await getAdapter().leaveRoom(...args);
    },
    [getAdapter],
  );
}
